import { useEffect, useState, type FormEvent } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import {
  useCreateProject,
  useDomains,
  usePatchProject,
  useZones,
} from '@/lib/queries';
import type { Project, ProjectPriority, ProjectStatus } from '@/lib/types';

type Props =
  | { open: boolean; onOpenChange: (next: boolean) => void; mode: 'create'; onCreated?: (id: string) => void }
  | { open: boolean; onOpenChange: (next: boolean) => void; mode: 'edit'; project: Project };

export default function ProjectForm(props: Props) {
  const { open, onOpenChange, mode } = props;
  const create = useCreateProject();
  const patch = usePatchProject(mode === 'edit' ? props.project.id : '');
  const domainsQ = useDomains();
  const zonesQ = useZones();

  const initial =
    mode === 'edit'
      ? {
          title: props.project.title,
          description: props.project.description ?? '',
          domainId: props.project.domainId ?? '',
          zoneId: props.project.zoneId ?? '',
          status: props.project.status,
          priority: props.project.priority,
        }
      : {
          title: '',
          description: '',
          domainId: '',
          zoneId: '',
          status: 'planned' as ProjectStatus,
          priority: 'medium' as ProjectPriority,
        };
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(initial);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!form.title.trim()) {
      setError('Title required.');
      return;
    }
    const body = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      domainId: form.domainId || null,
      zoneId: form.zoneId || null,
      status: form.status,
      priority: form.priority,
    };
    try {
      if (mode === 'create') {
        const created = await create.mutateAsync(body);
        props.onCreated?.(created.id);
      } else {
        await patch.mutateAsync(body);
      }
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{mode === 'create' ? 'New project' : 'Edit project'}</SheetTitle>
        </SheetHeader>
        <form onSubmit={onSubmit} className="space-y-4 pt-4 pb-2" data-testid="project-form">
          <div className="space-y-1.5">
            <Label className="font-mono text-[11px] uppercase tracking-wider">Title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              required
              autoFocus
              className="h-11 text-base"
              data-testid="project-title"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="font-mono text-[11px] uppercase tracking-wider">Status</Label>
              <select
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as ProjectStatus }))}
                className="h-11 w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="project-status"
              >
                <option value="planned">planned</option>
                <option value="active">active</option>
                <option value="blocked">blocked</option>
                <option value="completed">completed</option>
                <option value="abandoned">abandoned</option>
                <option value="archived">archived</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="font-mono text-[11px] uppercase tracking-wider">Priority</Label>
              <select
                value={form.priority}
                onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value as ProjectPriority }))}
                className="h-11 w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="project-priority"
              >
                <option value="low">low</option>
                <option value="medium">medium</option>
                <option value="high">high</option>
                <option value="urgent">urgent</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="font-mono text-[11px] uppercase tracking-wider">Domain</Label>
              <select
                value={form.domainId}
                onChange={(e) => setForm((f) => ({ ...f, domainId: e.target.value }))}
                className="h-11 w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">No domain</option>
                {(domainsQ.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="font-mono text-[11px] uppercase tracking-wider">Zone</Label>
              <select
                value={form.zoneId}
                onChange={(e) => setForm((f) => ({ ...f, zoneId: e.target.value }))}
                className="h-11 w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">No zone</option>
                {(zonesQ.data ?? []).map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="font-mono text-[11px] uppercase tracking-wider">Description</Label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={5}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-ring resize-y"
            />
          </div>
          {error ? <p className="text-sm text-warning">{error}</p> : null}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || patch.isPending} data-testid="project-submit">
              {(create.isPending || patch.isPending) ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
